import React, { useEffect, useState } from "react";
import { TableBody, TableCell, TableHead, TableSortLabel } from "@mui/material";
// eslint-disable-next-line no-unused-vars
import { Labels, Tags } from "../customersPage.styled";
import {
	StyledTable,
	TableWrapper,
	TableButton,
	StyledTableRow,
	StyledCheckbox,
	StyledTablePagination,
} from "../../../components/DataTable/DataTable.styles";
import { Link } from "react-router-dom";
import { getFormattedDate } from "../../../utils/DateUtils";
import { getNairaFormat } from "../../../utils/Utils";

/* eslint-disable react/prop-types */
function CustomersTable({ TableData, headCells, selected,  setSelected }) {
	const [order, setOrder] = useState("asc");
	const [orderBy, setOrderBy] = useState("");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	
	useEffect(() => {
		setSelected([]);
		setPage(0);
		setOrder("asc");
		setOrderBy("");
	}, [TableData]);

	const getVisibleRows = () => {
		function descendingComparator(a, b, orderBy) {
			if (b[orderBy] < a[orderBy]) {
				return -1;
			}
			if (b[orderBy] > a[orderBy]) {
				return 1;
			}
			return 0;
		}

		function getComparator(order, orderBy) {
			return order === "desc"
				? (a, b) => descendingComparator(a, b, orderBy)
				: (a, b) => -descendingComparator(a, b, orderBy);
		}

		// Check if TableData is not an array || TableData is empty
		if (Array.isArray(TableData)) {
			return TableData.slice().sort(getComparator(order, orderBy));
		} else return [];
	};

	const visibleRows = getVisibleRows().slice(
		page * rowsPerPage,
		page * rowsPerPage + rowsPerPage
	);

	const handleRequestSort = (property) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	const handleClick = (e, id) => {
		const selectedRow = TableData.find((row) => row.objectId === id);
		const selectedIndex = selected.findIndex(
			(row) => row.objectId === selectedRow.objectId
		);

		if (selectedIndex === -1) {
			setSelected((prevSelected) => [...prevSelected, selectedRow]);
		} else {
			setSelected((prevSelected) => [
				...prevSelected.slice(0, selectedIndex),
				...prevSelected.slice(selectedIndex + 1),
			]);
		}
	};

	const handleSelectAll = (e) => {
		if (e.target.checked) {
			const newSelected = TableData.map((row) => row);
			setSelected(newSelected);
			return;
		}
		setSelected([]);
	};

	const handleChangePage = (e, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (e) => {
		setRowsPerPage(+e.target.value);
		setPage(0);
	};

	return (
		<>
			<TableWrapper>
				<StyledTable sx={{ minWidth: 650 }}>
					<TableHead>
						<StyledTableRow>
							<TableCell padding='checkbox'>
								<StyledCheckbox
									color='default'
									checked={
										selected.length === TableData.length &&
										TableData.length !== 0
									}
									onChange={(e) => handleSelectAll(e)}
									inputProps={{ "aria-labelledby": "Select All" }}
								/>
							</TableCell>

							{headCells.map((headerCell) => (
								<TableCell
									key={headerCell.field}
									align='left'
									sx={{ minWidth: headerCell.width }}
								>
									<TableSortLabel
										active={orderBy === headerCell.field}
										onClick={() => handleRequestSort(headerCell.field)}
										direction={orderBy === headerCell.field ? order : "asc"}
									>
										{headerCell.title}
									</TableSortLabel>
								</TableCell>
							))}
						</StyledTableRow>
					</TableHead>

					<DataTableBody
						selected={selected}
						visibleRows={visibleRows}
						handleClick={handleClick}
						headCells={headCells}
					/>
				</StyledTable>
			</TableWrapper>

			<StyledTablePagination
				rowsPerPageOptions={[10, 20, 25, 100]}
				component='div'
				count={TableData.length}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
			/>
		</>
	);
}

const DataTableBody = ({ selected, visibleRows, handleClick, headCells }) => {
	const isSelected = (id) =>
		selected.findIndex((row) => row.objectId === id) !== -1;

	const TableRowItem = ({ row, headerCell }) => {
		// console.log(row);

		if (headerCell.field === "typeTransactions") {
			return (
				<TableButton
					component={Link}
					variant='outlined'
					size='small'
					to={`${row.objectId}`}
				>
					View
				</TableButton>
			);
		}

		if (headerCell.field === "typeCustomer") {
			return (
				<TableButton
					component={Link}
					variant='outlined'
					size='small'
					to={`./${row.objectId}/general`}
				>
					View
				</TableButton>
			);
		}

		if (headerCell.field === "createdAt") {
			return (
				<span className={headerCell.field}>
					{getFormattedDate(row["createdAt"])}
				</span>
			);
		}

		const AmountFields = [
			"amount",
			"price",
			"value",
			"deposit",
			"balance",
			"principal",
		];
		if (AmountFields.includes(headerCell.field)) {
			return (
				<span className={headerCell.field}>
					{getNairaFormat(row[headerCell.field])}
				</span>
			);
		}

		if (headerCell.field === "activeProfiles") {
			let mappedData = row[headerCell.field].map((item, index) => {
				return <Labels key={index} type={item} size={"small"} />;
			});

			return <div className='flex gap-2'>{mappedData}</div>;
		}

		const statusFields = ["profileStatus", "state", "paymentStatus"];
		if (statusFields.includes(headerCell.field)) {
			const status = row[headerCell.field] ? row[headerCell.field] : "Idle";

			switch (status) {
				case "complete":
					return <span className='badge complete'>{status}</span>;
				case "Idle":
					return <span className='badge idle'>{status}</span>;
				case "Incomplete":
					return <span className='badge incomplete'>{status}</span>;
				case "Inactive":
					return <span className='badge inactive'>{status}</span>;
				case "Deactivated":
					return <span className='badge deactivated'>{status}</span>;
				case "Flagged":
					return <span className='badge flagged'>{status}</span>;
				case "requested":
					return <span className='badge complete'>{status}</span>;
				case "default":
					return <span className='badge incomplete'>{status}</span>;
				case "ongoing":
					return <span className='badge warning'>{status}</span>;
				default:
					return <span className='badge default'>{status}</span>;
			}
		}

		const ImageFields = ["image", "secondImage"];
		if (ImageFields.includes(headerCell.field)) {
			return (
				<div className='img_container'>
					<img
						style={{
							height: "70px",
							width: "70px",
							objectFit: "cover",
						}}
						className=''
						src={row[headerCell.field]}
						alt='product image'
					/>
				</div>
			);
		}

		return (
			<span className={headerCell.field}>
				{row[headerCell.field] ? row[headerCell.field] : "---"}
			</span>
		);
	};

	return (
		<TableBody>
			{visibleRows.map((row) => {
				const isItemSelected = isSelected(row.objectId);
				return (
					<StyledTableRow
						hover
						key={row.objectId}
						onClick={(e) => handleClick(e, row.objectId)}
						role='checkbox'
						selected={isItemSelected}
					>
						<TableCell padding='checkbox'>
							<StyledCheckbox
								color='default'
								checked={isItemSelected}
								inputProps={{ "aria-labelledby": row.objectId }}
							/>
						</TableCell>

						{headCells.map((headerCell) => (
							<TableCell key={headerCell.field} align='left'>
								<TableRowItem row={row} headerCell={headerCell} />
							</TableCell>
						))}
					</StyledTableRow>
				);
			})}

			{visibleRows.length === 0 && (
				<StyledTableRow>
					<TableCell
						colSpan={headCells.length + 1}
						align='center'
						className='no_data_found'
					>
						NO DATA FOUND
					</TableCell>
				</StyledTableRow>
			)}
		</TableBody>
	);
};

export default CustomersTable;
